import { useMemo } from 'react'

import { getDayKeyInTimezone } from '@/components/integration/nylas/scheduler/helpers'
import useAvailabilityQuery from '@/components/integration/nylas/scheduler/hooks/useAvailabilityQuery'
import {
  AvailabilityFilter,
  EVERY_ADVISER_AVAILABILITY_FILTER,
  SchedulerAvailabilitySource,
  SchedulerOwningEntityType,
} from '@/components/integration/nylas/scheduler/types'

export interface SchedulerDay {
  /**
   * First thing on the day
   */
  date: Date
  /**
   * The timeslots available on the given day
   */
  timeSlotStartTimes: Date[]
}

interface UseSchedulerDaysResult {
  /**
   * Days and slot starts grouped by calendar day in the selected timezone.
   * `undefined` means availability has not loaded yet.
   */
  schedulerDays: SchedulerDay[] | undefined
  /**
   * Mirrors the underlying availability query loading state.
   */
  isLoading: boolean
  /**
   * True whenever a fetch is in flight, including refetches after an error.
   */
  isFetching: boolean
  /**
   * Mirrors the underlying availability query error state.
   */
  isAvailabilityFetchError: boolean
  /**
   * Triggers a fresh fetch of the underlying availability query.
   */
  refetch: () => void
}

/**
 * Builds scheduler-friendly availability from raw timeslots.
 *
 * Processing pipeline:
 * 1. Fetch raw slot data with `useAvailabilityQuery`.
 * 2. Group slot starts by calendar day in the selected IANA timezone.
 * 3. Sort slots within each day, then sort days chronologically.
 *
 * Important:
 * - Grouping uses timezone-aware date parts, so a UTC slot near midnight
 *   is placed on the correct local day for the selected timezone.
 * - The day `date` value is the earliest slot start in that local day,
 *   which gives deterministic day ordering.
 */
export default function useSchedulerDays(
  type: SchedulerOwningEntityType,
  owningEntityId: number | string,
  start: Date,
  end: Date,
  timezone: string,
  availabilityFilter: AvailabilityFilter = EVERY_ADVISER_AVAILABILITY_FILTER,
  options: {
    enabled?: boolean
    availabilitySource?: SchedulerAvailabilitySource
  } = {},
): UseSchedulerDaysResult {
  // Fetches slots shaped as `{ start: Date; end: Date }[]`.
  const availabilityQuery = useAvailabilityQuery(
    type,
    owningEntityId,
    start,
    end,
    availabilityFilter,
    {
      enabled: options.enabled,
      availabilitySource: options.availabilitySource,
    },
  )

  // Convert flat slot list into day buckets for the scheduler UI.
  const schedulerDays = useMemo(() => {
    // Keep `undefined` during initial load, so consumers can distinguish
    // "not loaded" from "loaded with zero available slots".
    if (availabilityQuery.data === undefined) {
      return undefined
    }

    // Key: local day string (YYYY-MM-DD in selected timezone)
    // Value: scheduler day aggregate.
    const groupedSchedulerDays = new Map<string, SchedulerDay>()

    for (const timeSlot of availabilityQuery.data.timeSlots) {
      // Group by the local calendar day that this slot start falls on.
      const dayKey = getDayKeyInTimezone(timeSlot.start, timezone)
      const existingSchedulerDay = groupedSchedulerDays.get(dayKey)

      if (existingSchedulerDay === undefined) {
        // First slot seen for this day starts a new group.
        groupedSchedulerDays.set(dayKey, {
          date: timeSlot.start,
          timeSlotStartTimes: [timeSlot.start],
        })
        continue
      }

      // Track the earliest start for this day; used for day sorting later.
      if (timeSlot.start.getTime() < existingSchedulerDay.date.getTime()) {
        existingSchedulerDay.date = timeSlot.start
      }

      // Collect additional starts belonging to the same local day.
      existingSchedulerDay.timeSlotStartTimes.push(timeSlot.start)
    }

    // 1) Sort starts within each day.
    // 2) Sort day groups by their earliest start.
    return Array.from(groupedSchedulerDays.values())
      .map((schedulerDay) => ({
        ...schedulerDay,
        timeSlotStartTimes: schedulerDay.timeSlotStartTimes.sort(
          (firstTimeSlotStart, secondTimeSlotStart) =>
            firstTimeSlotStart.getTime() - secondTimeSlotStart.getTime(),
        ),
      }))
      .sort(
        (firstSchedulerDay, secondSchedulerDay) =>
          firstSchedulerDay.date.getTime() - secondSchedulerDay.date.getTime(),
      )
  }, [availabilityQuery.data, timezone])

  return {
    schedulerDays,
    isLoading: availabilityQuery.isLoading,
    isFetching: availabilityQuery.isFetching,
    isAvailabilityFetchError: availabilityQuery.isError,
    refetch: availabilityQuery.refetch,
  }
}
